import { useEffect, useState } from "react";

import { Navigate } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";

/**
 * Route guard for `/app-messages`. Admits:
 *   - Admins (`role === "admin"`) - full access
 *   - ACTIVE module resellers (`type === "Reseller"` + `resellerType === "module"`
 *     + `resellerStatus === "ACTIVE"`) - full access
 *   - Regular resellers (`type === "Reseller"` and not module) - the page
 *     renders as a disabled marketing preview (no API calls, upgrade modal on
 *     entry)
 *
 * Regular users and non-ACTIVE module resellers are redirected to `/dashboard`.
 * This is **UX-only**: the real enforcement is `requireAdminOrService` on the
 * API, which rejects any token outside the admin/service/module-reseller set.
 * The client guard just avoids flashing a page that would then render with
 * empty data and toast-spam errors.
 */
export function canAccessAppMessagesRoute(user) {
  if (user?.role === "admin") return true;
  if (user?.type !== "Reseller") return false;

  if (user?.resellerType === "module") {
    return user?.resellerStatus === "ACTIVE";
  }

  return true;
}

const AppMessagesRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        setIsValidToken(false);
        setIsChecking(false);

        return;
      }

      try {
        const response = await apiService.validateToken(token);

        if (response) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
        }
      } catch {
        localStorage.removeItem("authToken");
        setIsValidToken(false);
      }

      setIsChecking(false);
    };

    validateToken();
  }, [user]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary-600 mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isValidToken || !canAccessAppMessagesRoute(user)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

export default AppMessagesRoute;
